import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './OTPPage.css';
import logo from '../../shared/assets/logopng.png'; // Replace with your actual logo path
import illustration from '../../shared/assets/otp_img.png'; // Replace with your OTP illustration path
import editIcon from '../../shared/assets/Vector.png'; // Replace with your actual edit icon image path
import { toast } from 'react-toastify';
import { authService } from './services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { MOBILE_BREAKPOINT, SPLASH_DURATION } from '../../shared/constants/uiConstants';
import { IoMailOpenOutline } from 'react-icons/io5';

const OTPPage = () => {
  const [otp, setOtp] = useState('');
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [timer, setTimer] = useState(300);
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpExpired, setIsOtpExpired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const phoneNumber = location?.state?.phoneNumber || '';
  const { login } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [showSplash, setShowSplash] = useState(false);
  const inputRef = useRef(null);

  const checkMobileAndShowSplash = useCallback(() => {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    if (isMobile) {
      setShowSplash(true);
      const timer = setTimeout(() => setShowSplash(false), SPLASH_DURATION);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    return checkMobileAndShowSplash();
  }, [checkMobileAndShowSplash]);


  useEffect(() => {
    // Timer countdown from 5 minutes (300 seconds)
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(countdown); // Clear the interval when timer reaches 0
            setIsResendEnabled(true); // Enable resend button when timer reaches 0
            setIsOtpExpired(true); // Mark OTP as expired
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdown); // Clean up the timer on component unmount
    }
  }, [timer]); // This useEffect runs every time the timer value changes

  const handleOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(val);
    if (val.length === 6 && !isLoading) verifyOtp(val);
  };

  const verifyOtp = async (otpString) => {
    if (isOtpExpired) {
      showSnackbar('Invalid OTP - OTP has expired', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const data = await authService.verifyOTP(phoneNumber, otpString);
      console.log('OTP verification response:', data);
      
      if (data.success) {
        // Extract token and user from correct location
        const token = data.data?.access_token;
        const user = data.data;
        console.log(`user details in otp section ${user}`);
        // Update AuthContext with login state
        const success = await login(token, user);
        if (success) {
          navigate('/dashboard');
        } else {
          showSnackbar('Failed to save login data. Please try again.', 'error');
        }
      } else {
        showSnackbar('Invalid OTP. Please try again.', 'error');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      showSnackbar('Failed to verify OTP. Please try again.', 'error',);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length === 6) verifyOtp(otp);
  };

  // Resend OTP functionality with API call
  const handleResendOtp = async () => {
    try {
      await authService.sendOTP(phoneNumber);
      setIsResendEnabled(false);
      setIsOtpExpired(false);
      setTimer(300); // Reset the timer to 5 minutes (300 seconds)
      setOtp('');
      toast.success('OTP sent successfully');
    } catch (error) {
      toast.error('Failed to send OTP. Please try again.');
    }
  };

  // Navigate to the edit phone number page
  const handleEditPhoneNumber = () => {
    navigate('/login'); // Navigate back to the login page
  };

  return (
    <div className="otp-page">

      {/* Splash Screen for Mobile */}
      {showSplash && (
        <div className="splash-screen">
          <img src={logo} alt="TykVen Logo" className="logoLogin" />
          <h1 className="app-title">TykVen Client</h1>
          <p className="app-subtitle">The Field Service Manager</p>
        </div>
      )}



  {/* Main Content */}
      {!showSplash && (
        <div className="content-wrapper">
        {/* Left Section for Desktop */}
        <div className="left-section">
          <img src={logo} alt="TykVen Logo" className="logo" />
          <h1 className="app-title">TykVen Client</h1>
          <p className="app-subtitle">The Field Service Manager</p>
        </div>

        {/* Right Section for OTP Form */}
        <div className="right-section">
          <form className="otp-form" onSubmit={handleVerifyOtp}>
            <img
              src={illustration}
              alt="OTP Illustration"
              className="form-illustration"
            />
            <h2 className="form-title">Verification Code</h2>
            <p className="form-subtitle">
              Enter the code from the SMS we sent to{' '}
              <strong>{phoneNumber}</strong>
              <img
                src={editIcon}
                alt="Edit Icon"
                className="edit-icon"
                onClick={handleEditPhoneNumber}
              />
            </p>

            {/* Timer Above OTP Input */}
            <div className="timer">
              {timer > 0 ? (
                <span>Resend in {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}</span>
              ) : (
                <span></span> // Empty when timer reaches 0
              )}
            </div>

            {/* OTP Input — single hidden input + 6 display boxes */}
            <div className="otp-input-group" onClick={() => inputRef.current?.focus()}>
              <input
                ref={inputRef}
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                value={otp}
                onChange={handleOtpChange}
                maxLength={6}
                className="otp-hidden-input"
                autoFocus
              />
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={`otp-box ${otp.length === i ? 'otp-box-active' : ''}`}>
                  {otp[i] || ''}
                </div>
              ))}
            </div>

            {/* Resend Button (below OTP input fields after timer reaches 0) */}
            {isResendEnabled && (
              <button
                type="button"
                className="resend-button"
                onClick={handleResendOtp}
              >
                I didn’t receive any code. <span style={{color: '#4960F9'}}>RESEND</span>
              </button>
            )}

            {isLoading ? (
                 <div className="otp-overlay">
                       <div className="otp-loader-box">
                         <div className="otp-spinner"></div>
                         <div className="otp-icon">
                           <IoMailOpenOutline />
                         </div>
                       </div>
                     </div>
            ) : (
              <button type="submit" className="verify-button">
                Submit
              </button>
            )}
          </form>
        </div>
      </div>
            )}
    </div>
  );
};

export default OTPPage;
